import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import { publicRequest, privateRequest } from "../utils/api";

// Types based on backend models and API responses
export interface AttractionTranslation {
  id?: number;
  attraction_id?: number;
  locale: string;
  name: string;
  description?: string;
  about?: string;
  slug: string;
}

export interface Attraction {
  id: string;
  title?: string;
  description?: string;
  about?: string;
  category?: string;
  image?: string;
  gallery?: string[];
  images?: string[];
  location?: string;
  locationImage?: string;
  practicalInfo?: {
    openingHours?: string;
    entranceFee?: string;
    accessibility?: string;
  };
  nearbyAttractions?: Array<{
    id?: string;
    title?: string;
    description?: string;
    image?: string;
  }>;
  featured?: boolean;
  latitude?: number;
  longitude?: number;
  status?: string;
  translations?: AttractionTranslation[];
  created_at?: string;
  updated_at?: string;
}

export interface AttractionsResponse {
  attractions: Attraction[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface AttractionResponse {
  attraction: Attraction;
  message?: string;
  status?: string;
}

export interface AttractionsQueryParams {
  per_page?: number;
  page?: number;
  locale?: string;
  search?: string;
  category?: string;
  featured?: boolean;
  admin?: string;
}

// Query keys
export const ATTRACTIONS_QUERY_KEY = ["attractions"];
export const ATTRACTION_QUERY_KEY = (id: number) => ["attractions", id];

// Fetch attractions with pagination
const fetchAttractions = async (
  params: AttractionsQueryParams = {}
): Promise<AttractionsResponse> => {
  // Convert boolean values to "1" or "0" strings for Laravel validation
  const queryParams: Record<string, unknown> = { ...params };
  if (queryParams.featured !== undefined) {
    queryParams.featured = queryParams.featured ? "1" : "0";
  }

  const response = await publicRequest.get("/attractions", {
    params: queryParams,
  });
  // Response structure: { success: true, data: { attractions: [...], pagination: {...} }, message: "..." }
  return response.data.data;
};

// Fetch single attraction
const fetchAttraction = async (id: number): Promise<Attraction> => {
  const response = await publicRequest.get(`/attractions/${id}`);
  // Response structure: { success: true, data: { attraction: {...} }, message: "..." }
  return response.data.data.attraction || response.data.data;
};

// Create attraction (admin only)
const createAttraction = async (attractionData: {
  category?: string;
  image?: string;
  gallery?: string[];
  location?: string;
  location_image?: string;
  opening_hours?: string;
  entrance_fee?: string;
  accessibility?: string;
  nearby_attractions?: Array<{
    id?: string;
    title?: string;
    description?: string;
    image?: string;
  }>;
  featured?: boolean;
  status?: string;
  latitude?: number;
  longitude?: number;
  translations: AttractionTranslation[];
}): Promise<Attraction> => {
  const response = await privateRequest.post("/attractions", attractionData);
  // Response structure: { success: true, data: { attraction: {...} }, message: "..." }
  return response.data.data.attraction || response.data.data;
};

// Update attraction (admin only)
const updateAttraction = async ({
  id,
  attractionData,
}: {
  id: number;
  attractionData: {
    category?: string;
    image?: string;
    gallery?: string[];
    location?: string;
    location_image?: string;
    opening_hours?: string;
    entrance_fee?: string;
    accessibility?: string;
    nearby_attractions?: Array<{
      id?: string;
      title?: string;
      description?: string;
      image?: string;
    }>;
    featured?: boolean;
    status?: string;
    latitude?: number;
    longitude?: number;
    translations?: AttractionTranslation[];
  };
}): Promise<Attraction> => {
  const response = await privateRequest.put(
    `/attractions/${id}`,
    attractionData
  );
  // Response structure: { success: true, data: { attraction: {...} }, message: "..." }
  return response.data.data.attraction || response.data.data;
};

// Delete attraction (admin only)
const deleteAttraction = async (id: number): Promise<void> => {
  await privateRequest.delete(`/attractions/${id}`);
};

// Custom hooks
export const useAttractions = (params: AttractionsQueryParams = {}) => {
  const result = useQuery({
    queryKey: [...ATTRACTIONS_QUERY_KEY, params],
    queryFn: () => fetchAttractions(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return result;
};

// Admin hook that includes translations
export const useAttractionsAdmin = (params: AttractionsQueryParams = {}) => {
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  return useQuery({
    queryKey: [
      ...ATTRACTIONS_QUERY_KEY,
      "admin",
      { ...params, locale: currentLanguage },
    ],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        per_page: (params.per_page || 15).toString(),
        page: (params.page || 1).toString(),
        locale: params.locale || currentLanguage,
        admin: "true",
        ...(params.search && { search: params.search }),
        ...(params.category && { category: params.category }),
        ...(params.featured !== undefined && {
          featured: params.featured ? "1" : "0",
        }),
      });

      const response = await privateRequest.get(`/attractions?${queryParams}`);
      // Return full response to match Events pattern
      return response.data;
    },
    staleTime: 0, // Always refetch for admin
    gcTime: 5 * 60 * 1000,
  });
};

export const useAttraction = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: ATTRACTION_QUERY_KEY(id),
    queryFn: () => fetchAttraction(id),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useAttractionAdmin = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: [...ATTRACTION_QUERY_KEY(id), "admin"],
    queryFn: async () => {
      const response = await privateRequest.get(
        `/attractions/${id}?admin=true`
      );
      // Response structure: { success: true, data: { attraction: {...} }, message: "..." }
      return response.data.data.attraction || response.data.data;
    },
    enabled,
    staleTime: 0, // Always refetch for admin
    gcTime: 5 * 60 * 1000,
  });
};

export const useCreateAttraction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAttraction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ATTRACTIONS_QUERY_KEY });
    },
  });
};

export const useUpdateAttraction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAttraction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ATTRACTIONS_QUERY_KEY });
    },
  });
};

export const useDeleteAttraction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAttraction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ATTRACTIONS_QUERY_KEY });
    },
  });
};
